import { useSecurityStore } from '~/stores/securityStore'
import { useAuthStore } from '~/stores/authStore'
import { useIframeStore } from '~/stores/chatAIStore';
import { useUserStore } from '~/stores/userStore'
import { computed, toValue, type MaybeRefOrGetter, type Ref } from 'vue'
import { breakpointsTailwind, useBreakpoints } from '@vueuse/core'
import { marked } from 'marked'
import DOMPurify from 'dompurify'

interface CompareResponse {
  comparison?: string
  [key: string]: any
}
interface OverviewResponse {
  product_overview?: string
  [key: string]: any
}

export const parseMarkdown = async (content?: string) => {
  if (!content) return ''

  const normalizedContent = content
    .replace(/\\r\\n/g, '\n')
    .replace(/\\n/g, '\n')
    .replace(/\\r/g, '\r')
  const html = marked.parse(normalizedContent, { breaks: true })
  const parsed = html instanceof Promise ? await html : html

  return DOMPurify.sanitize(parsed)
}

export const usePdpTgpt = (
  sku: MaybeRefOrGetter<string | undefined>,
  compareVisible: Ref<boolean>
) => {
  const authStore = useAuthStore()
  const config = useRuntimeConfig()
  const iframeStore = useIframeStore()
  const securityStore = useSecurityStore();
  const BASE_URL = config.public.tgptBaseUrl
  const dy = iframeStore.dynamicYieldData
  const isMobile = useBreakpoints(breakpointsTailwind).smaller('md')
  const isTaiaEnabled = computed(() => iframeStore.isEnableTaia)
  const dynamicYieldPdpData = computed(() =>
    isMobile.value
      ? iframeStore.dynamicYieldMobilePdpData
      : iframeStore.dynamicYieldPdpData
  )
  const userStore = useUserStore()
  const customerId = computed(() => userStore.data?.customerId !=null ? String(userStore.data.customerId) : null)
  const productSku = computed(() => toValue(sku)?.trim() || undefined)

  const getHeaders = async () => {

    if (!securityStore.data?.csrf) {
        await securityStore.fetch()
    }

    const csrf = securityStore.data?.csrf

    if (!csrf) {
        throw new Error("Invalid CSRF token/data.")
    }

    return {
        Authorization: `Bearer ${authStore.token}`,
        'Session-Id': authStore.sessionId,
        [csrf.headerName]: csrf.token
    }

   }

  const compareProduct = async (skuValue: string, customerId: string | null) => {
    await authStore.ensureSession()
    return await useNuxtApp().$siteFetch(`${BASE_URL}/product/compare`, {
      method: 'POST',
      body: {
        sku_id: skuValue,
        session_id: authStore.sessionId,
        customer_id: customerId,
        variant: dynamicYieldPdpData.value
      },
      headers: await getHeaders(),
      credentials: 'include'
    })
  }

  const productOverview = async (skuValue: string, customerId: string | null) => {
    await authStore.ensureSession()
    return await useNuxtApp().$siteFetch(`${BASE_URL}/product/overview`, {
      method: 'POST',
      body: {
        sku_id: skuValue,
        session_id: authStore.sessionId,
        customer_id: customerId,
        variant: dynamicYieldPdpData.value
      },
      headers: await getHeaders(),
      credentials: 'include'
    })
  }

const {
  data,
  pending: tgptPending,
  error: tgptError
} = useAsyncData(
  () => `tgpt-${productSku.value ?? 'empty'}`,
    async () => {
if (!productSku.value || !isTaiaEnabled.value) return null

const overviewPromise = productOverview(productSku.value, customerId.value).catch(() => null)

const comparePromise = compareVisible.value
  ? compareProduct(productSku.value, customerId.value).catch(() => null)
  : null

  const [compareRes, overviewRes] = await Promise.all([
    comparePromise,
    overviewPromise
  ])

  const sanitizedCompare = structuredClone(compareRes) as CompareResponse
  const sanitizedOverview = structuredClone(overviewRes) as OverviewResponse

  if (sanitizedCompare?.comparison) {
    sanitizedCompare.comparison = await parseMarkdown(
      sanitizedCompare.comparison
    )
  }

  if (sanitizedOverview?.product_overview) {
    sanitizedOverview.product_overview = await parseMarkdown(
      sanitizedOverview.product_overview
    )
  }
  return {
    compare: sanitizedCompare,
    overview: sanitizedOverview
  }
}, {
  server: false,
  immediate: true,
  watch: [
      productSku,
      isTaiaEnabled,
      customerId,
      dynamicYieldPdpData
    ]
})
  const overviewData = computed(() => data.value?.overview ?? null)
  const compareData = computed(() => data.value?.compare ?? null)



  const isOverviewVisible = computed(
    () => !!overviewData.value
  )

  const isCompareVisible = computed(
    () =>  !!compareData.value && compareVisible.value
  )

  const isTaiaSectionVisible = computed(
  () =>
    isTaiaEnabled.value &&
    (isOverviewVisible.value || isCompareVisible.value)
)

const defaultTab = computed(() => {
  if ((dy?.isComparisonDefault ?? false) && isCompareVisible.value) return 'compare'
  if (isOverviewVisible.value) return 'overview'
  if (isCompareVisible.value) return 'compare'
  return null
})

  return {
    compareData,
    overviewData,
    tgptPending,
    tgptError,
    isTaiaSectionVisible,
    defaultTab,
    isOverviewVisible,
    isCompareVisible,
  }
}
